/**
 * Rotating time-of-day greeting for the homepage Discovery panel's
 * personalized picks (DiscoveryPanel.jsx). Framework-free and fully
 * injectable (clock, storage, randomness) so the rotation rules are
 * unit-tested directly — see discoveryGreetings.test.js.
 *
 * Rotation rules:
 *  - One greeting per browser session: chosen once, then pinned in
 *    sessionStorage (plus a module-level cache, so rerenders, remounts and
 *    route changes never re-roll it — nor does StrictMode's double
 *    useState initializer).
 *  - A separate shuffle bag per time group: all 60 greetings are shuffled
 *    and dealt one per session; the bag is only reshuffled once every one
 *    of the 60 has been shown.
 *  - Two pools per group (see the greetings below): a draw for a row that
 *    isn't genuinely personalized is restricted to the EVERYONE pool, so a
 *    greeting never claims picks were chosen for someone when they weren't.
 *    The bag still holds all 60 ids either way — the pool filters at pick
 *    time — so the persisted shape is identical for both, and one shared
 *    deck keeps the no-repeat guarantee across a session that changes pool.
 *  - Never repeats any of the previous five greetings. When a new cycle
 *    starts, its first five slots are dealt from greetings outside that
 *    window, so the reshuffle can't bring back a recent greeting early.
 *  - Bag + recent history live in localStorage. Every storage access is
 *    guarded: blocked, missing or corrupted storage degrades to an
 *    in-memory pick for this page load, never an error.
 *  - Scoped per account (userKey): two people sharing a device each get
 *    their own bag, history and session greeting.
 *  - Synced per account across devices: the server's copy (GET/PUT
 *    /users/me/greeting-rotation, src/api/greetingRotation.js) is used when
 *    available and updated after each draw; the local copy is the fallback.
 */

// ── The greetings ───────────────────────────────────────────────────────
// Five time windows (timeGroupFor below), 60 greetings each. Every window is
// split into two pools, because the two make different promises:
//
//  - FOR_YOU says outright that these products were picked for this shopper.
//    It may only be drawn when the row really is personalized, so it is
//    small on purpose — a claim like that is worth making rarely and
//    honestly rather than often and approximately.
//  - EVERYONE claims nothing about where the products came from, so it is
//    safe for anyone, including a first-time visitor with no history at all.
//
// The pools are separate arrays rather than a flag per line so the split is
// visible at a glance and stays diffable against the source copy document.

const MORNING_FOR_YOU = [
  "A little morning inspiration, just for you.",
  "Begin with a surprise chosen for you.",
  "A touch of early inspiration, chosen for you.",
];

const MORNING_EVERYONE = [
  "Fresh finds for a fresh new day, {firstName}. ☀️",
  "Your morning just got a little more interesting.",
  "Start your day with something worth discovering.",
  "A bright new day calls for fresh finds.",
  "New day, new possibilities, {firstName}.",
  "Wake up to something you might enjoy.",
  "A fresh collection of possibilities awaits…",
  "Today’s first discoveries are ready for you…",
  "Start your day with a little inspiration.",
  "Something exciting could be waiting below… 👀",
  "Rise, shine and discover something new.",
  "Here’s a little brightness for your morning.",
  "Begin your day with a few delightful finds.",
  "Your morning picks have arrived, {firstName}…",
  "A new day brings new things to explore.",
  "Start today with something to smile about.",
  "Freshly picked possibilities for your morning.",
  "Let’s make your morning a little more exciting…",
  "Discover something that feels right for today…",
  "Your day is starting—and so is the discovery.",
  "Start bright with something you might enjoy.",
  "Today might be the day you find something special.",
  "Step into the day with fresh possibilities.",
  "Here’s something to put a smile on your morning.",
  "Your morning deserves a touch of discovery.",
  "Let’s begin with a few finds worth exploring…",
  "A fresh day and a fresh selection await…",
  "See what’s new for you this morning, {firstName}…",
  "A delightful start begins right here… ✨",
  "Begin the morning with something fresh.",
  "New possibilities are ready to greet you…",
  "Your first finds of the day are here.",
  "Bring a little discovery into your morning.",
  "Start the day on a curious note.",
  "A fresh dose of inspiration is waiting…",
  "Let’s discover something wonderful this morning…",
  "The morning feels brighter with something new.",
  "Your early discoveries are ready, {firstName}…",
  "A little something to brighten your start.",
  "Your morning selection is looking lovely.",
  "Before the day gets busy, explore something new.",
  "A new day deserves a new discovery.",
  "Here’s a cheerful pick-me-up for your morning.",
  "Freshly selected and ready to explore…",
  "Start curious and see where it takes you.",
  "Bring a little joy to the beginning of your day.",
  "Let today begin with something interesting.",
  "Your morning moment of discovery starts here…",
  "New favourites could be waiting below…",
  "Ready to discover something fresh, {firstName}?",
  "Take a bright new look at today’s selection…",
  "A few thoughtful finds to start your day.",
  "Open the day with a little inspiration.",
  "Your morning discoveries are just getting started.",
  "Something new could make today even brighter.",
  "Explore something delightful this morning…",
  "Your day begins with fresh possibilities.",
];

const MIDDAY_FOR_YOU = [
  "Midday finds, chosen with you in mind.",
  "See what we found for your midday, {firstName}… 👀",
  "A fresh midday edit, selected for you.",
];

const MIDDAY_EVERYONE = [
  "A little discovery to brighten your midday.",
  "Your midday picks are ready, {firstName}…",
  "Take a midday pause and explore something new…",
  "Fresh possibilities for the middle of your day.",
  "Something special is waiting at midday…",
  "Halfway through the day, with more to discover.",
  "A quick midday browse might surprise you.",
  "Make room for a little midday inspiration.",
  "A few fresh finds for your midday break.",
  "Your midday moment of discovery begins here…",
  "Let’s add something interesting to your day…",
  "Discover something delightful this midday…",
  "The day is in full swing—and so is the discovery.",
  "Fresh inspiration for the heart of your day.",
  "Pause, browse and see what catches your eye…",
  "Midday is a great time to discover something new.",
  "A small find could brighten the rest of your day.",
  "Here’s something worth seeing this midday.",
  "Keep your day moving with a few fresh finds.",
  "A thoughtfully selected midday collection awaits…",
  "New finds have arrived for the middle of your day…",
  "The middle of your day deserves a treat.",
  "Let’s make the middle of your day more interesting…",
  "Today’s midday selection is ready to explore…",
  "Bring a little curiosity into your midday.",
  "Fresh discoveries for a well-timed pause.",
  "Something new could be waiting just below…",
  "Explore a few midday possibilities, {firstName}…",
  "A little inspiration for the middle of your day.",
  "Take five and see what’s new…",
  "Fresh midday finds are ready for you…",
  "A little midday treat is waiting below…",
  "Your midday collection has arrived…",
  "A thoughtful selection for this part of your day.",
  "Take a moment and discover something interesting…",
  "New discoveries are ready when you are…",
  "Perfect timing for a quick browse.",
  "Your midday deserves a touch of inspiration.",
  "Fresh ideas and interesting finds await…",
  "Your next favourite might be a midday discovery.",
  "Add a little spark to the middle of your day.",
  "The middle of the day brings new possibilities.",
  "Your midday lineup is ready to explore…",
  "Something interesting, right on time…",
  "Refresh your day with a few new discoveries.",
  "Pause for a moment—something lovely awaits…",
  "Make the rest of your day a little more exciting.",
  "A few well-timed finds for you, {firstName}.",
  "Discover something at your own pace this midday…",
  "A midday surprise might be waiting below…",
  "Keep your day feeling fresh with something new.",
  "Something delightful could brighten your midday.",
  "Explore what’s new in the middle of your day…",
  "Your midday inspiration starts here…",
  "Take a closer look at today’s midday picks…",
  "Good things come to those who browse.",
  "Right now feels like a good time to explore.",
];

const AFTERNOON_FOR_YOU = [
  "See what we found for you, {firstName}… 👀",
  "A little afternoon inspiration, selected for you.",
];

const AFTERNOON_EVERYONE = [
  "Treat your afternoon, {firstName}. ✨",
  "A little discovery could brighten your afternoon.",
  "Take a quick look at what caught our eye…",
  "Your next great find might be right here…",
  "Add something interesting to your afternoon.",
  "Something special is waiting to be discovered…",
  "A few fresh possibilities for your afternoon.",
  "Make your afternoon a little more exciting.",
  "A little inspiration for the rest of your day.",
  "Your afternoon picks are ready to explore…",
  "Pause for a moment and discover something new…",
  "A delightful find could be just around the corner.",
  "Brighten your afternoon with something interesting.",
  "Plenty of day left to find something special.",
  "Your afternoon just found a little extra sparkle.",
  "Take a break and see what’s waiting for you…",
  "A few finds for wherever the day takes you.",
  "Something here might be exactly what you need.",
  "Keep the day going with a little inspiration.",
  "Here’s something worth exploring this afternoon.",
  "Your next discovery could be closer than you think.",
  "Let’s add a little excitement to your afternoon…",
  "Fresh finds have landed for your afternoon.",
  "A quick browse could lead to something wonderful.",
  "Discover something made for moments like this…",
  "There’s always room for one more great discovery.",
  "Your afternoon selection is looking rather lovely.",
  "Let’s see what brings a smile to your afternoon… 😊",
  "The afternoon is yours—see what catches your eye.",
  "New ideas to carry you through the day.",
  "A little curiosity could lead somewhere lovely.",
  "Find something that makes your afternoon brighter.",
  "Your afternoon edit is ready, {firstName}…",
  "Add a fresh discovery to your day.",
  "A few thoughtful picks for a relaxed afternoon.",
  "A new favourite could be one look away.",
  "Let your curiosity guide your afternoon.",
  "Take another look—something interesting awaits…",
  "An afternoon full of possibilities begins here…",
  "Your next find is ready when you are…",
  "Give your afternoon a fresh touch.",
  "Explore a selection chosen to inspire you…",
  "There’s something new to discover this afternoon.",
  "Keep the afternoon interesting, {firstName}.",
  "A little inspiration can go a long way.",
  "Take your time and see what stands out.",
  "Fresh finds for the next part of your day.",
  "Your afternoon deserves a pleasant surprise.",
  "Find something that feels just right.",
  "A thoughtful afternoon selection is waiting…",
  "New discoveries can happen at any moment.",
  "See what could make your afternoon more enjoyable…",
  "A fresh collection has arrived for you…",
  "Make a little room for something new.",
  "Explore today’s afternoon highlights…",
  "Your afternoon favourites may be waiting below…",
  "Discover something different this afternoon…",
  "Keep the good energy going this afternoon.",
];

const EVENING_FOR_YOU = [
  "Evening finds, selected with you in mind.",
  "This evening’s picks, chosen with you in mind.",
];

const EVENING_EVERYONE = [
  "Time to unwind, {firstName}. 🌆",
  "Your evening discoveries are ready…",
  "Wind down with something worth exploring.",
  "A few lovely finds to complete your day.",
  "The day isn’t over—there’s more to discover.",
  "Settle in and see what catches your eye.",
  "Something special for your evening.",
  "End your day with a little inspiration.",
  "Relax and discover something interesting.",
  "Your evening just got a little more exciting.",
  "Take your time—there’s something lovely to explore.",
  "A calm evening and a few delightful finds.",
  "Here’s something to make your evening brighter.",
  "Put your feet up and see what we found.",
  "A few fresh possibilities before the day ends.",
  "Explore something special at your own pace…",
  "Your evening picks have arrived, {firstName}…",
  "Finish the day with something that makes you smile.",
  "There’s always time for one more discovery.",
  "Make your evening a little more delightful.",
  "Something interesting is waiting below…",
  "Let the evening lead you to something new.",
  "A relaxed browse could turn up a gem.",
  "Here’s a little inspiration for your evening.",
  "Discover something lovely as the day winds down…",
  "Your perfect evening find might be right here… ✨",
  "Evenings are better with something new.",
  "Slow down and enjoy a few fresh discoveries.",
  "Your evening edit is ready to explore…",
  "Find something special before the day is done.",
  "A little evening inspiration goes a long way.",
  "Settle into the evening with something interesting.",
  "Your next discovery is waiting, {firstName}…",
  "A few thoughtful finds for a peaceful evening.",
  "The day’s slowing down—the finds aren’t.",
  "Explore something that adds joy to your evening…",
  "Your evening selection has arrived…",
  "Take a relaxed look at what’s new…",
  "There’s something delightful waiting for you…",
  "Let curiosity brighten your evening.",
  "A fresh discovery could complete your day.",
  "Enjoy a slower moment with something new.",
  "Evening possibilities, ready when you are…",
  "Here’s something worth discovering this evening.",
  "See what could make your evening more enjoyable…",
  "A little something to complement your evening.",
  "Fresh finds for a well-deserved pause.",
  "Your evening deserves a pleasant surprise.",
  "Relax, explore and see what feels right.",
  "A few beautiful possibilities await…",
  "End the day on an inspired note.",
  "Something fresh has arrived for your evening…",
  "Take a moment to explore, {firstName}…",
  "Let the day settle and discover something new.",
  "Your evening favourites may be waiting below…",
  "A calm moment can lead to a wonderful discovery.",
  "Bring a little sparkle to your evening.",
  "The perfect evening discovery could be just ahead.",
];

const NIGHT_FOR_YOU = [
  "A little late-night inspiration, chosen for you.",
  "Night-time finds, selected with you in mind.",
  "See what we found for you tonight, {firstName}…",
];

const NIGHT_EVERYONE = [
  "One last look might reveal your next favourite. 👀",
  "Tonight might bring your next great discovery.",
  "Something lovely before you call it a night.",
  "Your night-time picks are ready, {firstName}… 🌙",
  "A quiet night calls for something worth exploring.",
  "Settle in and discover something new tonight.",
  "Something special is waiting for you tonight…",
  "Your night just became a little more interesting.",
  "Explore a few finds before the day is done…",
  "Still up? There’s more to discover.",
  "Let the quiet of the night inspire something new.",
  "Tonight’s selection is ready to explore…",
  "A peaceful browse could uncover a gem.",
  "Something delightful might be waiting below…",
  "Your next favourite could be a late-night find.",
  "Take your time—the night is yours.",
  "A few thoughtful finds for this quiet hour.",
  "End your day with something worth remembering.",
  "The night brings a fresh set of possibilities.",
  "Discover something new at your own pace…",
  "A little night-time curiosity can go a long way.",
  "Here’s something to make your night brighter.",
  "Your late-night discoveries have arrived…",
  "Explore what’s waiting before you call it a day…",
  "Something interesting is still waiting to be found…",
  "Let tonight lead you to something unexpected.",
  "A quiet moment and a few fresh discoveries.",
  "There’s always room for a little more inspiration.",
  "Your evening may be over, but discovery continues.",
  "Calm nights are made for browsing.",
  "Something lovely could be only a scroll away.",
  "Finish your day with a fresh discovery.",
  "A few night-time possibilities are waiting…",
  "Explore something that catches your eye…",
  "Your night selection is ready when you are…",
  "Let the day end with something interesting.",
  "A little inspiration for the quieter hours.",
  "The perfect night-time find might be right here…",
  "Take one more look before signing off.",
  "Fresh discoveries do not always wait for daylight.",
  "Find something that makes tonight more enjoyable.",
  "A night of new possibilities begins here…",
  "Something worth discovering is waiting below…",
  "Your night deserves a pleasant surprise.",
  "One more look could make your night.",
  "Take a quiet moment to explore…",
  "Tonight’s fresh finds are ready for you…",
  "Follow your curiosity into the night.",
  "A little something special before you rest.",
  "Night-time inspiration, right on cue.",
  "See what stands out in tonight’s selection…",
  "Something new could make this night memorable.",
  "Browse at your own pace, {firstName}…",
  "A peaceful night and a few interesting finds.",
  "Let tonight end with a little inspiration.",
  "Your final discoveries of the day are ready…",
  "The night is calm—and there’s more to explore.",
];

// Stable ids ("morning-01" … "night-60") — what gets persisted, so the
// wording of a line can be edited without invalidating anyone's history.
// Ids run through FOR_YOU first and then EVERYONE, so they are positional
// across the pair: reordering either list re-points stored history at a
// different sentence. That is cosmetic and self-healing (sanitizeRotation
// drops ids it no longer knows, and `recent` only suppresses five), but it
// is the reason to append to a pool rather than insert into one.
// `forYou` travels on the entry rather than being recomputed at the call
// site, so a greeting can never be drawn into the wrong pool.
const toEntries = (group, forYou, everyone) =>
  [...forYou.map((text) => [text, true]), ...everyone.map((text) => [text, false])].map(
    ([text, isForYou], i) => ({
      id: `${group}-${String(i + 1).padStart(2, "0")}`,
      text,
      forYou: isForYou,
    })
  );

export const GREETINGS = {
  morning: toEntries("morning", MORNING_FOR_YOU, MORNING_EVERYONE),
  midday: toEntries("midday", MIDDAY_FOR_YOU, MIDDAY_EVERYONE),
  afternoon: toEntries("afternoon", AFTERNOON_FOR_YOU, AFTERNOON_EVERYONE),
  evening: toEntries("evening", EVENING_FOR_YOU, EVENING_EVERYONE),
  night: toEntries("night", NIGHT_FOR_YOU, NIGHT_EVERYONE),
};

export const GREETING_GROUPS = Object.keys(GREETINGS);
export const RECENT_LIMIT = 5;
export const SESSION_KEY = "hr_greeting";
export const ROTATION_KEY = "hr_greeting_rotation";

// Per-account storage keys ("hr_greeting:42"); the bare key only when no
// account id is known.
const scoped = (key, userKey) => (userKey == null || userKey === "" ? key : `${key}:${userKey}`);
export const sessionKeyFor = (userKey) => scoped(SESSION_KEY, userKey);
export const rotationKeyFor = (userKey) => scoped(ROTATION_KEY, userKey);

const BY_ID = new Map(Object.values(GREETINGS).flat().map((g) => [g.id, g]));
const IDS_BY_GROUP = Object.fromEntries(
  GREETING_GROUPS.map((group) => [group, GREETINGS[group].map((g) => g.id)])
);

/** Visitor's local device time: 05:00–10:59 morning, 11:00–13:59 midday,
 *  14:00–16:59 afternoon, 17:00–20:59 evening, 21:00–04:59 night — so
 *  `night` is the window that spans midnight. batchDayFor() in
 *  src/utils/discoveryRotation.js attributes a past-midnight visit to the
 *  previous day by naming that group, so the two must agree. */
export function timeGroupFor(date = new Date()) {
  const hour = date.getHours();
  if (hour >= 5 && hour < 11) return "morning";
  if (hour >= 11 && hour < 14) return "midday";
  if (hour >= 14 && hour < 17) return "afternoon";
  if (hour >= 17 && hour < 21) return "evening";
  return "night";
}

/** First name from the account's free-text `name` — the only name field a
 *  user has. Returns null rather than ever falling back to an email,
 *  username or a stringified "undefined"/"null". */
export function firstNameFrom(fullName) {
  if (typeof fullName !== "string") return null;
  const trimmed = fullName.trim();
  if (!trimmed || trimmed.includes("@")) return null;
  const first = trimmed.split(/\s+/)[0].replace(/[.,;:!?]+$/, "");
  if (!/\p{L}/u.test(first) || /^(undefined|null)$/i.test(first)) return null;
  return first;
}

const PLACEHOLDER = "{firstName}";

/** Fills {firstName}, or — with no usable name — removes the placeholder
 *  together with its attached comma and spacing ("…new day, {firstName} ☀️"
 *  → "…new day ☀️"). split/join rather than String.replace so a name
 *  containing "$&" etc. is inserted literally. */
export function formatGreeting(template, firstName) {
  if (firstName) return template.split(PLACEHOLDER).join(firstName);
  return template
    .replace(/\s*,\s*\{firstName\}/g, "")
    .replace(/\{firstName\}\s*,?\s*/g, "")
    .replace(/\s{2,}/g, " ")
    .trim();
}

// One emoji, including a variation selector or ZWJ-joined sequence.
const EMOJI_RUN = /(\p{Extended_Pictographic}(?:\uFE0F|\u200D\p{Extended_Pictographic}\uFE0F?)*)/u;

/** Splits a greeting into text and emoji parts, so the emoji can be
 *  rendered aria-hidden — decoration, not something a screen reader
 *  should read out ("…right here, sparkles"). */
export function splitDecorativeEmoji(text) {
  return text
    .split(EMOJI_RUN)
    .filter(Boolean)
    .map((part) => ({ text: part, emoji: EMOJI_RUN.test(part) }));
}

/** The greeting as renderable parts — literal text, decorative emoji, and
 *  the first name on its own so it can carry its own type treatment
 *  (.discovery-greeting-name in DiscoveryPanel.css).
 *
 *  Splits the *template* on the placeholder before anything else touches
 *  it, so the name never reaches the emoji regex and never passes through a
 *  replace: a name containing "$&", "<b>" or even "{firstName}" is carried
 *  through literally, and an emoji inside a name stays audible (a name is
 *  content, not decoration). Punctuation directly after the name joins the
 *  name part (so it is gold too); the comma before it, the spaces and any
 *  emoji stay in the literal segments — and joining every part's text reproduces
 *  formatGreeting(template, firstName) exactly, which is what keeps the
 *  rendered sentence, and what a screen reader hears, unchanged.
 *
 *  With no usable name it delegates to formatGreeting's stripping path and
 *  emits no name part at all — never an empty one. */
// Punctuation directly after the name takes the name's colour (owner,
// 2026-10-11): "Hello, Sungu!" with only "Sungu" gold left the "!" white, a
// separate block of colour. Only marks touching the name move; the comma
// before the name, spaces and emoji stay outside.
const TRAILING_MARKS = /^[!?.,…;:]+/;

export function greetingParts(template, firstName) {
  if (!firstName) return splitDecorativeEmoji(formatGreeting(template, null));
  const parts = [];
  template.split(PLACEHOLDER).forEach((segment, i) => {
    let rest = segment;
    if (i > 0) {
      const marks = (rest.match(TRAILING_MARKS) || [""])[0];
      parts.push({ text: firstName + marks, name: true });
      rest = rest.slice(marks.length);
    }
    parts.push(...splitDecorativeEmoji(rest));
  });
  return parts.filter((p) => p.text !== "");
}

// ── Guarded storage access — the window.*Storage getter itself can throw
// (e.g. SecurityError when site data is blocked), not just get/setItem. ──
const browserStorage = (name) => () => {
  try {
    return window[name] ?? null;
  } catch {
    return null;
  }
};

const readItem = (getStore, key) => {
  try {
    return getStore()?.getItem(key) ?? null;
  } catch {
    return null;
  }
};

const writeItem = (getStore, key, value) => {
  try {
    getStore()?.setItem(key, value);
  } catch {}
};

// Keeps only known, de-duplicated ids for each group — anything else
// (malformed JSON, wrong shapes, ids from a since-edited list) is dropped,
// which repairs the stored state on the next write instead of failing.
// Applied to both the local copy and the server's. `seq` counts draws, so
// the copy that has progressed further can be told apart (see pickCopy).
function sanitizeRotation(parsed) {
  const seq = Number.isInteger(parsed?.seq) && parsed.seq >= 0 ? parsed.seq : 0;
  const state = { seq };
  for (const group of GREETING_GROUPS) {
    const known = new Set(IDS_BY_GROUP[group]);
    const raw = parsed && typeof parsed === "object" ? parsed[group] : null;
    const clean = (list) =>
      Array.isArray(list) ? [...new Set(list.filter((id) => typeof id === "string" && known.has(id)))] : [];
    state[group] = { bag: clean(raw?.bag), recent: clean(raw?.recent).slice(-RECENT_LIMIT) };
  }
  return state;
}

function loadRotation(getLocal, key) {
  try {
    return sanitizeRotation(JSON.parse(readItem(getLocal, key)));
  } catch {
    return sanitizeRotation(null);
  }
}

// Which copy to continue from: the server's (shared by every device on the
// account) unless this device's own copy has drawn further — which only
// happens when a save from here never reached the server (e.g. offline).
function pickCopy(local, server) {
  if (!server) return { chosen: local, other: null };
  return server.seq >= local.seq ? { chosen: server, other: local } : { chosen: local, other: server };
}

// The other copy's recent greetings are skipped for this pick too — so a
// greeting shown on a device whose save was lost still can't come straight
// back — but never removed from the deck: a merely stale copy (another
// device has drawn since) must not cost this cycle any greetings. They go
// first, as the oldest, so the kept five stay the chosen copy's latest.
function withOtherRecents(chosenGroup, otherGroup) {
  if (!otherGroup) return chosenGroup;
  const extra = otherGroup.recent.filter((id) => !chosenGroup.recent.includes(id));
  return { bag: chosenGroup.bag, recent: [...extra, ...chosenGroup.recent] };
}

function shuffle(list, random) {
  const out = [...list];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.min(i, Math.floor(random() * (i + 1)));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

// A fresh cycle: the first RECENT_LIMIT slots are dealt only from greetings
// outside the recent window, and the rest (recent ones included) are
// shuffled behind them — so a recent greeting can't resurface within five
// draws of its last showing, however the shuffle falls.
function newCycle(ids, recent, random) {
  const shuffled = shuffle(ids, random);
  const lead = shuffled.filter((id) => !recent.includes(id)).slice(0, RECENT_LIMIT);
  const rest = shuffled.filter((id) => !lead.includes(id));
  return [...lead, ...shuffle(rest, random)];
}

// `allowed` is the pool filter (see getSessionGreeting's `pool`). Ids the
// pool excludes stay in the bag rather than being dropped from it: the deck
// is shared by both pools, so a line skipped while the row isn't
// personalized is still waiting when it is. They collect at the front and
// get stepped over, and once nothing drawable is left the deck is treated as
// spent and reshuffled — which is what stops a bag holding only excluded ids
// from stalling.
function draw({ bag, recent }, ids, random, allowed = () => true) {
  let deck = bag;
  let pick = deck.find((id) => allowed(id) && !recent.includes(id));
  if (!pick) {
    deck = newCycle(ids, recent, random);
    // Prefer a drawable id from outside the recent window — newCycle puts
    // those first — but never return nothing if the lead is all excluded.
    pick = deck.find((id) => allowed(id) && !recent.includes(id)) ?? deck.find(allowed);
  }
  return {
    pick,
    next: {
      bag: deck.filter((id) => id !== pick),
      recent: [...recent.filter((id) => id !== pick), pick].slice(-RECENT_LIMIT),
    },
  };
}

// In-memory pick per account for this page load.
const sessionGreetings = new Map();

/** This browser session's greeting for one account, drawn at most once.
 *  Returns `{ id, text, fresh }` — `fresh` is true only on the call that
 *  actually drew it, so the panel can reveal a new greeting without
 *  replaying that animation on every remount or unrelated rerender.
 *  `remote` is the account's server-side rotation (null/undefined when
 *  there is none or it couldn't be fetched); `onPersist` receives the new
 *  full rotation after a draw, to save back to the server.
 *  `pool` is "all" when the row really is personalized, or "everyone" to
 *  exclude the lines that say outright that the picks were chosen for this
 *  shopper. */
export function getSessionGreeting({
  userKey = null,
  remote = null,
  onPersist = null,
  pool = "all",
  now = new Date(),
  getLocal = browserStorage("localStorage"),
  getSession = browserStorage("sessionStorage"),
  random = Math.random,
} = {}) {
  const cacheKey = userKey == null ? "" : String(userKey);
  const stale = (greeting) => ({ ...greeting, fresh: false });
  const group = timeGroupFor(now);

  // The pin holds a greeting steady for as long as its own part of the day
  // lasts — a tab opened at 11:50 keeps its morning line for those ten
  // minutes, but a tab still open at 15:00 must not still be saying "good
  // morning". Ids carry their group ("afternoon-07"), so the stored pin
  // already says which part of the day it belongs to; nothing new is
  // stored, and a pin written by an older build still reads correctly.
  const belongsToNow = (id) => typeof id === "string" && id.slice(0, id.indexOf("-")) === group;

  // A pin can outlive the reason it was allowed: a session that was
  // personalized when it drew a "chosen for you" line may not be by the time
  // the page is reloaded. So the pool is re-checked on read, not only on the
  // draw, and such a pin is redrawn rather than kept.
  const allowed = (id) => pool === "all" || BY_ID.get(id)?.forYou !== true;

  const cached = sessionGreetings.get(cacheKey);
  if (cached && belongsToNow(cached.id) && allowed(cached.id)) return stale(cached);

  const sessionKey = sessionKeyFor(userKey);
  const pinned = readItem(getSession, sessionKey);
  if (pinned && BY_ID.has(pinned) && belongsToNow(pinned) && allowed(pinned)) {
    sessionGreetings.set(cacheKey, BY_ID.get(pinned));
    // Pinned from an earlier page load in this session — not a new draw.
    return stale(BY_ID.get(pinned));
  }

  const rotationKey = rotationKeyFor(userKey);
  const local = loadRotation(getLocal, rotationKey);
  const server = remote && typeof remote === "object" ? sanitizeRotation(remote) : null;
  const { chosen, other } = pickCopy(local, server);
  const { pick, next } = draw(
    withOtherRecents(chosen[group], other?.[group]),
    IDS_BY_GROUP[group],
    random,
    allowed
  );
  const rotation = { ...chosen, seq: chosen.seq + 1, [group]: next };
  writeItem(getLocal, rotationKey, JSON.stringify(rotation));
  writeItem(getSession, sessionKey, pick);
  sessionGreetings.set(cacheKey, BY_ID.get(pick));
  try {
    onPersist?.(rotation);
  } catch {} // syncing is best effort; the greeting is already chosen
  return { ...BY_ID.get(pick), fresh: true };
}

/** Smallest the greeting is allowed to get. Below this a heading stops
 *  being readable, so a greeting that still doesn't fit wraps to a second
 *  line instead of shrinking further (only the longest lines, in the
 *  narrowest columns).
 *  Tied to the greeting's face, not a generic "readable size": 18px is where
 *  Cormorant Garamond's short lowercase (x-height ~0.4em) reads at caption
 *  scale, and at 18px every greeting fits in two lines down to a 320px phone.
 *  The CSS clamp floor and --greeting-min (DiscoveryPanel.css) must equal it. */
export const GREETING_MIN_PX = 18;

/** The font size that puts a greeting on one line.
 *  Text width scales with font size, so the fitting size is the current one
 *  scaled by how much too wide it is; `wraps` says the floor got in the way.
 *  Pure, so the rule is testable without a layout engine. */
export function fitGreetingSize({ maxPx, availablePx, measuredPx, minPx = GREETING_MIN_PX }) {
  if (!(maxPx > 0) || !(availablePx > 0) || !(measuredPx > 0)) return null;
  if (measuredPx <= availablePx) return null;                    // already one line
  const scaled = Math.floor(((maxPx * availablePx) / measuredPx) * 10) / 10;
  return { size: Math.max(minPx, scaled), wraps: scaled < minPx };
}

/** Test-only: forget the in-memory picks (simulates a fresh page load). */
export function __resetGreetingCacheForTests() {
  sessionGreetings.clear();
}
